import { useEffect, useRef } from 'react';
import logo from '../assets/logo-petito.png';
import babyLayer from '../assets/logo-petito-baby.png';
import leafLayer from '../assets/logo-petito-leaf.png';

type LogoSize = 'sm' | 'md' | 'lg' | 'xl';
/**
 * `css` — layered leaf blow-in + sleeping baby (playful brand motion)
 * `static` — single original mark, no motion
 * `rock` — single original mark with simple whole-logo rock (legacy)
 */
type LogoMode = 'css' | 'static' | 'rock';

const sizeClass: Record<LogoSize, string> = {
    sm: 'petito-logo-anim--sm h-12 w-12 sm:h-14 sm:w-14',
    md: 'petito-logo-anim--md h-24 w-24 sm:h-28 sm:w-28',
    // Welcome hero: compact on phone so the plate doesn’t bury the photo; desktop unchanged
    lg: 'petito-logo-anim--lg h-[min(112px,32vw)] w-[min(112px,32vw)] sm:h-[240px] sm:w-[240px] lg:h-[310px] lg:w-[310px]',
    xl: 'petito-logo-anim--xl h-[min(134px,38vw)] w-[min(134px,38vw)] sm:h-[260px] sm:w-[260px]',
};

export type AnimatedPetitoLogoProps = {
    mode?: LogoMode;
    /** Extra bounce on layered / rock motion */
    playful?: boolean;
    size?: LogoSize;
    className?: string;
    imgClassName?: string;
    alt?: string;
    /** Decorative only (e.g. next to a visible “Petito” wordmark) */
    decorative?: boolean;
};

/**
 * Petito mark with optional CSS animation (original motion / layout).
 *
 * GPU only — does not change keyframes, box size, or centering:
 * pause continuous loops when off-screen or tab hidden.
 * Never pauses leaf/baby-slot intro (freezing those caused the bottom offset).
 */
const AnimatedPetitoLogo = ({
    mode = 'css',
    playful = true,
    size = 'sm',
    className = '',
    imgClassName = '',
    alt = 'Petito',
    decorative = false,
}: AnimatedPetitoLogoProps) => {
    const rootRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (mode === 'static') return;
        const el = rootRef.current;
        if (!el) return;

        // Desktop: no play-state thrash logic — leave animation exactly as designed
        const mobileMq = window.matchMedia('(max-width: 1023px)');
        if (!mobileMq.matches) {
            el.classList.remove('petito-logo-anim--paused');
            return;
        }

        // Start true so we never pause on first paint before IO runs
        let inView = true;
        const introUntil = performance.now() + 2500;

        const syncPaused = () => {
            if (!mobileMq.matches) {
                el.classList.remove('petito-logo-anim--paused');
                return;
            }
            // Let leaf + cradle intro run fully before any pause is allowed
            if (performance.now() < introUntil) {
                el.classList.remove('petito-logo-anim--paused');
                return;
            }
            el.classList.toggle('petito-logo-anim--paused', document.hidden || !inView);
        };

        document.addEventListener('visibilitychange', syncPaused);
        mobileMq.addEventListener?.('change', syncPaused);

        let io: IntersectionObserver | null = null;
        if (typeof IntersectionObserver !== 'undefined') {
            io = new IntersectionObserver(
                ([entry]) => {
                    inView =
                        (entry?.intersectionRatio ?? 0) > 0.02 || (entry?.isIntersecting ?? false);
                    syncPaused();
                },
                { threshold: [0, 0.02, 0.1], rootMargin: '64px' }
            );
            io.observe(el);
        }

        const introTimer = window.setTimeout(syncPaused, 2550);

        return () => {
            document.removeEventListener('visibilitychange', syncPaused);
            mobileMq.removeEventListener?.('change', syncPaused);
            io?.disconnect();
            window.clearTimeout(introTimer);
            el.classList.remove('petito-logo-anim--paused');
        };
    }, [mode]);

    if (mode === 'static') {
        return (
            <img
                src={logo}
                alt={decorative ? '' : alt}
                aria-hidden={decorative || undefined}
                className={`object-contain ${sizeClass[size]} ${className} ${imgClassName}`.trim()}
            />
        );
    }

    if (mode === 'rock') {
        return (
            <span
                ref={rootRef}
                className={`petito-logo-anim ${sizeClass[size]} ${playful ? 'petito-logo-anim--playful' : ''} ${className}`.trim()}
                aria-hidden={decorative || undefined}
            >
                <img
                    src={logo}
                    alt={decorative ? '' : alt}
                    className={`petito-logo-anim__mark ${imgClassName}`.trim()}
                />
                {playful && (
                    <>
                        <span className="petito-logo-anim__sparkle petito-logo-anim__sparkle--heart" aria-hidden>
                            ♥
                        </span>
                        <span className="petito-logo-anim__sparkle petito-logo-anim__sparkle--star" aria-hidden>
                            ★
                        </span>
                    </>
                )}
            </span>
        );
    }

    // Layered: leaf blows under + baby sleeps
    // fit wraps stage + sparkles so lg/xl scale(0.9) keeps ♥/★ next to the mark
    // (same relative placement as header/login). Stage still clips leaf intro only.
    return (
        <span
            ref={rootRef}
            className={`petito-logo-anim ${sizeClass[size]} ${playful ? 'petito-logo-anim--playful' : ''} ${className}`.trim()}
            aria-hidden={decorative || undefined}
        >
            <span className="petito-logo-anim__fit">
                <span className="petito-logo-anim__stage">
                    <span className="petito-logo-anim__cradle">
                        <img
                            src={leafLayer}
                            alt=""
                            width={630}
                            height={630}
                            className="petito-logo-anim__leaf"
                            draggable={false}
                            decoding="async"
                        />
                        <span className="petito-logo-anim__baby-slot">
                            <img
                                src={babyLayer}
                                alt={decorative ? '' : alt}
                                width={640}
                                height={640}
                                className={`petito-logo-anim__baby ${imgClassName}`.trim()}
                                draggable={false}
                                decoding="async"
                            />
                        </span>
                    </span>
                </span>
                {playful && (
                    <>
                        <span className="petito-logo-anim__sparkle petito-logo-anim__sparkle--heart" aria-hidden>
                            ♥
                        </span>
                        <span className="petito-logo-anim__sparkle petito-logo-anim__sparkle--star" aria-hidden>
                            ★
                        </span>
                    </>
                )}
            </span>
        </span>
    );
};

export default AnimatedPetitoLogo;
